// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Lo Scadenziario, il motore di tutto"
// Centro assistenza → guida per amministratori (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, GestioneScadenziario — ricerca ("Cerca per
//                    oggetto o tipo attività..."), filtro ambito, filtro
//                    stato Attive/Obsolete/Tutte, "Nuova attività".
//   Form ........... "Oggetto da monitorare *", "Tipo attività *",
//                    "Sotto-attività (opzionale)". Card "Frequenza e limiti":
//                    "Frequenza (giorni)" — segnaposto "es. 1 = giornaliero,
//                    7 = settimanale, 30 = mensile", sotto: "Lascia vuoto se
//                    l'attività è a esecuzione manuale (non ha scadenza)".
//                    Solo con frequenza 1 compaiono "Salta sabato" e "Salta
//                    domenica". "Valore minimo" / "Valore massimo": dal
//                    20/08/2026 campi di testo con tastierino decimale
//                    (virgola accettata e normalizzata in handleSalva; prima
//                    erano type="number" e 0,5 diventava 5). Card persone:
//                    "Monitoraggio", "— Aggiungi supplente —", "Verifica",
//                    "Verificatori aggiuntivi (opzionale, multipli)". Note.
//   Duplicato ...... POST/PUT rispondono 409 se (oggetto, tipo) esiste gia':
//                    dialogo "Attività duplicata?" con "Salvare comunque?".
//   Frequenza vuota. nella lista attivita' dell'operatore diventa il chip
//                    "Controllo continuo": sempre in lista, mai in scadenza.
//   Lista .......... chip "Ogni Ngg" / "Manuale", Min/Max, chi monitora,
//                    supplenti, "Verifica: X (+ aggiuntivi)", "Obsoleta".
//   Formazione ..... la via piccola-azienda decisa in docs/FORMAZIONE.md:
//                    persona/gruppo come oggetto + formazione a scadenziario.
//                    Questa guida la documenta (capitolo dedicato).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'z0', label: 'Il motore di tutto' },
  { id: 'z1', label: '1. Anatomia di una voce' },
  { id: 'z2', label: '2. La frequenza' },
  { id: 'z3', label: '3. Le soglie' },
  { id: 'z4', label: '4. Le persone' },
  { id: 'z5', label: '5. Doppioni e obsolete' },
  { id: 'z6', label: '6. La formazione a scadenziario' },
  { id: 'z7', label: 'Le trappole' },
  { id: 'z8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const CampoFinto = ({ etichetta, valore, segnaposto, sotto, larghezza }) => (
  <div style={{ marginBottom: 10, width: larghezza }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '8px 10px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || segnaposto || '—'}</div>
    {sotto && <div style={{ fontSize: 11, color: LT.n500, marginTop: 3 }}>{sotto}</div>}
  </div>
);

const PillolaFinta = ({ children, accesa }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 999,
    border: `1px solid ${accesa ? LT.blu : LT.n200}`,
    background: accesa ? LT.bluSoft : LT.n0, color: accesa ? LT.blu : LT.n600,
  }}>
    {accesa && <Ico d={I.check} size={12} stroke={LT.blu} />}
    {children}
  </span>
);

export default function Scadenziario() {
  return (
    <GuidaLayout
      titolo="Lo Scadenziario, il motore di tutto"
      sottotitolo="Chi controlla cosa, ogni quanto e con quali soglie: da qui nascono le attività che gli operatori si trovano davanti ogni mattina — e i ruoli di tutti."
      durata="~ 13 minuti"
      destinatario="Per amministratori"
      sezioni={SEZIONI}
    >

      {/* z0 */}
      <H id="z0">Il motore di tutto</H>

      <P>
        Lo <Code>Scadenziario</Code> è il posto dove il piano di autocontrollo smette di
        essere un documento e diventa un meccanismo. Ogni voce dice quattro cose:{' '}
        <Strong>cosa</Strong> si controlla (l'oggetto), <Strong>che controllo</Strong> è
        (il tipo di attività), <Strong>ogni quanto</Strong> torna, e{' '}
        <Strong>chi</Strong> lo fa e chi lo riverifica. Da queste voci nascono da sole le
        liste del mattino degli operatori, le code dei verificatori, e perfino i ruoli:
        come raccontano le guide precedenti, si <em>è</em> monitoratori o verificatori
        perché una voce di qui vi nomina.
      </P>
      <P>
        È anche la schermata dove un errore pesa di più: una frequenza sbagliata o una
        soglia sbagliata non rovinano una registrazione — le rovinano <Strong>tutte, da
        qui in avanti</Strong>. Vale la pena capirla bene.
      </P>

      {/* z1 */}
      <H id="z1" cat="produzione">1. Anatomia di una voce</H>

      <P>
        <Tasto>Nuova attività</Tasto> apre il modulo. In testa la coppia obbligatoria:{' '}
        <Strong>Oggetto da monitorare</Strong> e <Strong>Tipo attività</Strong> — la Cella
        1 e il controllo temperatura, il piano di lavoro e la sanificazione. La{' '}
        <Strong>Sotto-attività</Strong> è la specifica in più quando lo stesso tipo si
        declina («sanificazione di fine turno» vs «sanificazione settimanale a fondo»).
      </P>

      <Callout kind="info" titolo="Se l'oggetto non c'è ancora">
        La strada comoda parte dall'altra schermata: si crea l'oggetto e dalla sua scheda
        si preme <Tasto>Assegna attività</Tasto> — si atterra qui con l'oggetto già
        selezionato. Una voce per volta: se la cella ha tre controlli, sono tre voci.
      </Callout>

      {/* z2 */}
      <H id="z2" cat="temperature">2. La frequenza</H>

      <MockFrame
        url="app.labthree.it — Scadenziario · Frequenza e limiti"
        markers={[
          { n: 1, top: '26%', left: '85%', titolo: 'I giorni',
            testo: '1 = tutti i giorni, 7 = una volta a settimana, 30 = mensile. Vuoto = nessuna scadenza: l’attività resta sempre disponibile, da registrare quando serve.' },
          { n: 2, top: '58.9%', left: '63%', titolo: 'Il fine settimana',
            testo: 'Solo per le giornaliere: se il sabato o la domenica si è chiusi, il controllo non va in scadenza — e il lunedì non compare un finto ritardo.' },
          { n: 3, top: '79%', left: '79%', titolo: 'Le soglie, con la virgola',
            testo: 'Min e max accettano virgola o punto: 0,5 e -18,5 si scrivono come si dicono. La coerenza col valore misurato la farà rispettare la schermata di registrazione.' },
        ]}
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.lg, padding: 16, maxWidth: 470, margin: '0 auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, marginBottom: 12 }}>
              Frequenza e limiti
            </div>
            <CampoFinto
              etichetta="Frequenza (giorni)"
              valore="1"
              sotto="Lascia vuoto se l'attività è a esecuzione manuale (non ha scadenza)"
            />
            <div style={{ margin: '4px 0 12px' }}>
              <div style={{
                fontSize: 10.5, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6,
              }}>Giorni saltati</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <PillolaFinta accesa>Salta sabato</PillolaFinta>
                <PillolaFinta>Salta domenica</PillolaFinta>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <CampoFinto etichetta="Valore minimo" valore="0" larghezza="50%" />
              <CampoFinto etichetta="Valore massimo" valore="4" larghezza="50%" />
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        La frequenza è in giorni, e il segnaposto del campo è già il bigino: 1 giornaliero,
        7 settimanale, 30 mensile. Con la frequenza a 1 compaiono{' '}
        <Tasto>Salta sabato</Tasto> e <Tasto>Salta domenica</Tasto>, per chi il fine
        settimana è chiuso.
      </P>
      <P>
        <Strong>Vuota</Strong> è un caso a parte, e va capito: l'attività non ha scadenza,
        non va mai «in ritardo», e nella lista dell'operatore compare{' '}
        <Strong>sempre</Strong>, col contrassegno <Code>Controllo continuo</Code> e l'ora
        dell'ultima esecuzione. Serve per i controlli legati agli eventi e non al
        calendario: la temperatura a ogni ricevimento merce, il controllo a ogni avvio
        macchina.
      </P>

      {/* z3 */}
      <H id="z3" cat="temperature">3. Le soglie</H>

      <P>
        <Strong>Valore minimo</Strong> e <Strong>Valore massimo</Strong> trasformano
        l'attività in un controllo <em>misurato</em>: da quel momento l'operatore, per
        registrare, dovrà scrivere il valore, e la schermata di registrazione farà
        rispettare la coerenza — un valore fuori soglia non si può salvare come conforme,
        né viceversa. Si scrivono come si dicono: <Code>0,5</Code> o <Code>0.5</Code>,{' '}
        <Code>-18,5</Code> per i negativi. Anche una soglia sola è legittima: solo il
        massimo per una cella positiva, solo il minimo dove serve.
      </P>

      <Callout kind="warn" titolo="Cambiare una soglia non riscrive il passato">
        Le registrazioni già fatte restano giudicate con le soglie di allora, ed è
        giusto così. Ma da qui in avanti la nuova soglia governa tutto: prima di
        cambiarla, un giro nello Storico a vedere quanto ci si passava vicino evita di
        trasformare la settimana prossima in una collezione di non conformità.
      </Callout>

      {/* z4 */}
      <H id="z4" cat="formazione">4. Le persone</H>

      <Passo n="1" titolo="Monitoraggio, e i supplenti">
        <P style={{ margin: '4px 0' }}>
          <Strong>Monitoraggio</Strong> è il titolare: l'attività compare nella sua lista
          del mattino. Con <Strong>— Aggiungi supplente —</Strong> si nominano i
          sostituti, che la vedono anche loro: è ciò che tiene coperto il controllo
          quando il titolare è in ferie — e ciò che fa comparire ad alcuni «attività non
          mie» (la FAQ della guida sui monitoraggi risponde proprio così).
        </P>
      </Passo>

      <Passo n="2" titolo="Verifica, e gli aggiuntivi">
        <P style={{ margin: '4px 0' }}>
          <Strong>Verifica</Strong> è chi riverifica le registrazioni (guida sulle
          Verifiche); i <Strong>Verificatori aggiuntivi</Strong> sono i suoi sostituti.
          Meglio se diverso da chi monitora: due firme uguali una sopra l'altra valgono
          poco.
        </P>
      </Passo>

      <Callout kind="info" titolo="È da qui che nascono i «permessi»">
        Nominare qualcuno in questi campi è ciò che gli accende le voci di menu: la prima
        assegnazione fa di lui un monitoratore o un verificatore, al primo ingresso
        successivo. E quando qualcuno lascia l'azienda, è qui che si torna a riassegnare —
        disattivarlo in Organico non libera i suoi compiti.
      </Callout>

      {/* z5 */}
      <H id="z5" cat="pulizie">5. Doppioni e obsolete</H>

      <P>
        Se salvate una voce con la stessa coppia oggetto + tipo di una già esistente, il
        programma si ferma e chiede: <em>«Attività duplicata? Esiste già…
        Salvare comunque?»</em>. Quasi sempre la risposta giusta è no — il doppione fa
        comparire il controllo due volte nella lista del mattino. Il «sì» ha senso solo
        nei casi voluti, tipicamente con sotto-attività diverse.
      </P>
      <P>
        Le voci superate si rendono <Strong>obsolete</Strong>, non si cancellano — stessa
        filosofia di oggetti e persone: lo storico resta intero. Il filtro in cima
        (<Tasto>Attive</Tasto> / <Tasto>Obsolete</Tasto> / <Tasto>Tutte</Tasto>) le tiene
        fuori dai piedi senza perderle. E quando si rende obsoleto un intero oggetto, la
        seconda domanda di quella schermata può chiudere qui le sue voci in un colpo.
      </P>

      {/* z6 */}
      <H id="z6" cat="formazione">6. La formazione a scadenziario</H>

      <P>
        Per una realtà piccola, lo scadenziario copre bene anche le scadenze delle{' '}
        <Strong>persone</Strong>: si crea l'operatore (o il gruppo) come oggetto da
        monitorare — «Anna Bianchi», collocazione «addetta produzione» — e le si cuciono
        addosso le voci: rinnovo attestato ogni 1095 giorni, sorveglianza sanitaria ogni
        365. Da lì in poi scadenze, registrazioni con N.A. e storico funzionano come per
        qualsiasi cella.
      </P>

      <Callout kind="tip" titolo="Il trucco è chi lo monitora">
        Come responsabile del monitoraggio di queste voci conviene mettere{' '}
        <Strong>chi gestisce la formazione</Strong> — di solito l'amministratore — non la
        persona stessa: è lui che deve vedersi comparire «rinnovo attestato di Anna» in
        lista, e registrarlo a cosa fatta. Per le aziende con molte persone è allo studio
        un modulo dedicato, con piano di formazione e curriculum.
      </Callout>

      {/* z7 */}
      <H id="z7">Le trappole</H>

      <Callout kind="warn" titolo="La voce senza persone">
        Oggetto e tipo sono obbligatori, le persone no: una voce senza monitoratore non
        compare nella lista di <Strong>nessuno</Strong>, e il controllo esiste solo sulla
        carta. Dopo ogni giro di modifiche, un colpo d'occhio alla lista: ogni voce attiva
        deve avere il suo nome accanto.
      </Callout>

      <Callout kind="warn" titolo="La frequenza «tanto per»">
        Mettere 1 a tutto sembra prudente e produce l'effetto opposto: liste del mattino
        chilometriche, operatori che spuntano in fretta, registro gonfio e meno
        difendibile. La frequenza giusta è quella del piano di autocontrollo — che è
        anche quella che l'ispettore si aspetta di veder rispettata, non superata a caso.
      </Callout>

      <Callout kind="warn" titolo="Il fine settimana dimenticato">
        Azienda chiusa il sabato ma «Salta sabato» spento: ogni lunedì i controlli
        giornalieri compaiono in ritardo di un giorno, e a furia di finti ritardi nessuno
        guarda più quelli veri. Due tocchi qui sistemano il lunedì per sempre.
      </Callout>

      {/* z8 */}
      <H id="z8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Che differenza c'è fra frequenza vuota e frequenza lunga?">
          Una voce a 365 giorni va in scadenza una volta l'anno, e se non si fa risulta in
          ritardo. Una voce vuota non scade mai: è sempre disponibile e nessuno la
          sollecita. La prima è per i controlli dovuti, la seconda per quelli legati agli
          eventi — se «va fatto», dategli una frequenza.
        </FAQ>

        <FAQ q="Posso cambiare il titolare di venti voci in una volta?">
          No, oggi si modifica voce per voce: quando qualcuno lascia, il giro di
          riassegnazioni va fatto a mano. Il filtro di ricerca aiuta a trovare in fretta
          tutte le voci con il suo nome.
        </FAQ>

        <FAQ q="Perché non trovo le soglie sull'oggetto?">
          Perché appartengono al controllo, non all'oggetto: la stessa cella può avere il
          controllo temperatura con 0–4 °C e la sanificazione senza soglie. È il disegno
          spiegato nella guida sugli oggetti — e il posto dove si impostano è questo.
        </FAQ>

        <FAQ q="Ho appena assegnato un'attività ma l'operatore non la vede">
          Se era già dentro col PIN, deve uscire e rientrare: i compiti si caricano
          all'ingresso. Se la voce non è giornaliera, comparirà quando arriva la sua
          scadenza — con «Mostra tutto» la può vedere anche prima.
        </FAQ>

        <FAQ q="Modificare una voce cambia lo storico?">
          No: le registrazioni fatte restano com'erano, con le soglie e le persone di
          allora. La voce di scadenziario governa il futuro, lo Storico custodisce il
          passato — è il motivo per cui si rende obsoleto invece di cancellare.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>La mia azienda</Strong>: i dati e il logo che finiscono in testa a
            tutte le stampe appena viste.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
